/** Environments an application can be created with, in display order. */
export const APPLICATION_ENVIRONMENTS = [
  'dev',
  'test',
  'prod',
  // Last: few applications want one
  'sandbox',
] as const;
export type ApplicationEnvironment = (typeof APPLICATION_ENVIRONMENTS)[number];

/** The Azure subscription chosen for each environment. */
export type EnvironmentSubscriptions = Partial<
  Record<ApplicationEnvironment, string>
>;

type EnvironmentChoice = {
  environments: readonly string[];
  environmentSubscriptions?: EnvironmentSubscriptions;
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The known environments among `environments`, in display order. */
export const chosenEnvironments = (environments: readonly string[]) =>
  APPLICATION_ENVIRONMENTS.filter(env => environments.includes(env));

/** `environments` with `environment` ticked or unticked, in display order. */
export function toggleEnvironment(
  environments: readonly string[],
  environment: ApplicationEnvironment,
  checked: boolean,
): ApplicationEnvironment[] {
  return APPLICATION_ENVIRONMENTS.filter(env =>
    env === environment ? checked : environments.includes(env),
  );
}

/** The subscriptions bootstrap-ivm.sh set up for the IVM, by kind. */
export type OnboardedSubscriptionIds = { nonprod?: string; prod?: string };

/** The subscription bootstrap-ivm.sh set up for `environment`; undefined when the user picks one. */
export const onboardedSubscription = (
  environment: ApplicationEnvironment,
  onboarded: OnboardedSubscriptionIds,
) => (environment === 'prod' ? onboarded.prod : onboarded.nonprod) || undefined;

/** `subscriptions` with each environment that has an onboarded subscription set to it. */
export function withOnboardedSubscriptions(
  environments: readonly ApplicationEnvironment[],
  subscriptions: EnvironmentSubscriptions,
  onboarded: OnboardedSubscriptionIds,
): EnvironmentSubscriptions {
  const next = { ...subscriptions };
  for (const environment of environments) {
    next[environment] =
      onboardedSubscription(environment, onboarded) ?? next[environment];
  }
  return next;
}

/** Which chosen environment's subscription holds the shared resources: dev first, sandbox last. */
const SHARED_SUBSCRIPTION_PREFERENCE: readonly ApplicationEnvironment[] = [
  'dev',
  'test',
  'sandbox',
];

/**
 * The request's subscription fields. One non-prod environment's subscription
 * also holds the shared resources (ACR, CI identity), so it is the IVM's
 * non-prod subscription; prod has its own.
 */
export function subscriptionFields({
  environments,
  environmentSubscriptions = {},
}: EnvironmentChoice) {
  const chosen = chosenEnvironments(environments);
  const home = SHARED_SUBSCRIPTION_PREFERENCE.find(env => chosen.includes(env));
  return {
    nonprodSubscriptionId: home ? environmentSubscriptions[home] ?? '' : '',
    prodSubscriptionId: chosen.includes('prod')
      ? environmentSubscriptions.prod ?? ''
      : '',
  };
}

/**
 * request-identity.yml's `environment_subscriptions` input: the non-prod
 * environments outside the non-prod subscription, e.g. `test=<uuid>`; '' when none.
 */
export function environmentSubscriptionsInput(
  choice: EnvironmentChoice,
): string {
  const { nonprodSubscriptionId } = subscriptionFields(choice);
  const subscriptions = choice.environmentSubscriptions ?? {};
  return chosenEnvironments(choice.environments)
    .filter(env => env !== 'prod')
    .filter(
      env => subscriptions[env] && subscriptions[env] !== nonprodSubscriptionId,
    )
    .map(env => `${env}=${subscriptions[env]}`)
    .join(',');
}

/** First problem with the chosen environments and subscriptions; '' when there is none. */
export function environmentChoiceError(choice: EnvironmentChoice): string {
  const chosen = chosenEnvironments(choice.environments);
  if (chosen.length === 0) {
    return 'Choose at least one environment.';
  }
  if (!chosen.some(env => env !== 'prod')) {
    return 'Choose dev, test or sandbox as well: the CI identity is created in a non-production subscription.';
  }
  for (const env of chosen) {
    const subscriptionId = (
      choice.environmentSubscriptions?.[env] ?? ''
    ).trim();
    if (!subscriptionId) {
      return `Choose an Azure subscription for ${env}.`;
    }
    if (!UUID_RE.test(subscriptionId)) {
      return `The subscription ID for ${env} must be a valid UUID.`;
    }
  }
  return '';
}
